import { cleanupWizardService } from '@/api/cleanupWizard';
import { Button, Icon, Typography } from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import { ModalHeader } from '@/components/dashboard';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import useModal from '@/hooks/useModal';
import { StatusCleanupWizard } from '@/interfaces';

interface ActionsOptions {
	icon: string;
	value: StatusCleanupWizard;
	tooltipText: string;
}

interface ActionsTableColumnProps {
	userId: string;
	status: StatusCleanupWizard;
	refetch: () => void;
}

export const ActionsTableColumn: React.FC<ActionsTableColumnProps> = ({
	userId,
	status,
	refetch,
}) => {
	const { toastError, toastSuccess } = useNotification();
	const {
		Modal: ModalDeactivateAccount,
		hide: hideDeactivateAccount,
		show: showDeactivateAccount,
	} = useModal();

	const {
		Modal: ModalReactivateAccount,
		hide: hideReactivateAccount,
		show: showReactivateAccount,
	} = useModal();

	const handleActions = (actions: StatusCleanupWizard) => {
		switch (actions) {
			case 'Deactivate Account':
				showDeactivateAccount();
				break;
			case 'Contact Customer':
				handleContactCustomer();
				break;
			case 'Review Required':
				handleVerifyCustomer();
				break;
			default:
				break;
		}
	};

	const actionsOptions: ActionsOptions[] = [
		{
			icon: Icons.messages,
			value: 'Contact Customer',
			tooltipText: 'Contact customer',
		},
		{
			icon: Icons.profileTick,
			value: 'Review Required',
			tooltipText: 'Customer verified',
		},
		{
			icon: Icons.profileDelete,
			value: 'Deactivate Account',
			tooltipText: 'Deactivate account',
		},
	];

	const handleContactCustomer = async () => {
		try {
			await cleanupWizardService.putContactCustomer(userId);
			refetch();
			toastSuccess(
				<span>
					{`The status has been successfully changed to 'Contact Customer'.`}{' '}
					<br />
					You may proceed with the next steps.
				</span>
			);
		} catch (error) {
			toastError(
				`The status could not be updated to 'Contact Customer'. Please try again.`
			);
		}
	};

	const handleVerifyCustomer = async () => {
		try {
			await cleanupWizardService.putVerifyCustomer(userId);
			refetch();
			toastSuccess('The client has been successfully verified.');
		} catch (error) {
			toastError('The client could not be verified. Please try again.');
		}
	};

	const handleDeactivateAccount = async () => {
		try {
			await cleanupWizardService.putDeactivateAccount(userId);
			refetch();
			toastSuccess('The account has been successfully deactivated.');
		} catch (error) {
			toastError('The account could not be deactivated. Please try again.');
		} finally {
			hideDeactivateAccount();
		}
	};

	const handleReactivateAccount = async () => {
		try {
			await cleanupWizardService.putReactivateAccount(userId);
			refetch();
			toastSuccess('The account has been successfully reactivated.');
		} catch (error) {
			toastError('The account could not be reactivated. Please try again.');
		} finally {
			hideReactivateAccount();
		}
	};

	return (
		<div>
			<ul className="flex flex-wrap gap-4">
				{status === 'Deactivate Account' ? (
					<li>
						<TooltipComponent
							className="w-fit"
							label="Reactivate account"
							trigger={
								<button
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										showReactivateAccount();
									}}
								>
									<Icon
										src={Icons.userAdd}
										className="text-primary"
										width={24}
										height={24}
									/>
								</button>
							}
						/>
					</li>
				) : (
					actionsOptions.map((item, index) => (
						<li key={'actions-cleanup-wizard-options' + index}>
							<TooltipComponent
								className="w-fit"
								label={item.tooltipText}
								trigger={
									<button
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											handleActions(item.value as StatusCleanupWizard);
										}}
									>
										<Icon
											src={item.icon}
											className="text-primary"
											width={24}
											height={24}
										/>
									</button>
								}
							/>
						</li>
					))
				)}
			</ul>
			<ModalDeactivateAccount>
				<ModalHeader title="Account Deactivation Confirmation" />
				<Typography
					type="regular-16"
					className="text-center my-8 max-w-[406px]"
				>
					{`Deactivating this account will delete the client's details, and the
					user will lose access. This action cannot be undone.`}
				</Typography>
				<div className="flex items-center w-full justify-center gap-x-2">
					<Button
						size="fit"
						label="Cancel"
						className="!border-green-base"
						onClick={hideDeactivateAccount}
					/>
					<Button
						size="fit"
						label="Deactivate"
						className="!border-main-red !text-main-red"
						onClick={handleDeactivateAccount}
					/>
				</div>
			</ModalDeactivateAccount>
			<ModalReactivateAccount>
				<ModalHeader title="Account Reactivation Confirmation" />
				<Typography
					type="regular-16"
					className="text-center my-8 max-w-[406px]"
				>
					{`Reactivating this account will restore the client's details, and the user will regain access to their account.`}
				</Typography>
				<div className="flex items-center w-full justify-center gap-x-2">
					<Button
						size="fit"
						label="Cancel"
						className="!border-green-base"
						onClick={hideReactivateAccount}
					/>
					<Button
						size="fit"
						label="Reactivate"
						decoration="primary"
						onClick={handleReactivateAccount}
					/>
				</div>
			</ModalReactivateAccount>
		</div>
	);
};
